import { Controller } from "@hotwired/stimulus";

type Subscription = { id: number; limit: number | null; name: string; productName: string };

export default class extends Controller {
  static targets = ["form", "unsavedIndicator", "resetButton", "banner", "bannerList"];
  static values = {
    subscriptions: String
  };

  declare subscriptionsValue: string;
  declare formTarget: HTMLFormElement;
  declare unsavedIndicatorTarget: HTMLElement;
  declare resetButtonTarget: HTMLButtonElement;
  declare bannerTarget: HTMLElement;
  declare bannerListTarget: HTMLElement;
  declare hasBannerTarget: boolean;
  declare hasBannerListTarget: boolean;

  private boundValidateForm: (event: Event) => void;
  private boundHandleChange: () => void;
  private boundHandleReset: () => void;
  private initialState: Map<string, boolean> = new Map();

  connect() {
    this.boundValidateForm = this.validateForm.bind(this);
    this.boundHandleChange = this.handleChange.bind(this);
    this.boundHandleReset = this.handleReset.bind(this);

    this.formTarget.addEventListener("submit", this.boundValidateForm);
    this.formTarget.addEventListener("change", this.boundHandleChange);
    this.resetButtonTarget.addEventListener("click", this.boundHandleReset);

    this.captureInitialState();
    this.updateBanner();
  }

  disconnect() {
    this.formTarget.removeEventListener("submit", this.boundValidateForm);
    this.formTarget.removeEventListener("change", this.boundHandleChange);
    this.resetButtonTarget.removeEventListener("click", this.boundHandleReset);
  }

  private captureInitialState() {
    const checkboxes = this.formTarget.querySelectorAll<HTMLInputElement>(".permission-matrix-checkbox");
    checkboxes.forEach((cb) => {
      this.initialState.set(cb.name, cb.checked);
    });
  }

  private handleChange() {
    this.updateUnsavedIndicator();
    this.updateBanner();
  }

  private handleReset() {
    setTimeout(() => {
      this.updateUnsavedIndicator();
      this.updateBanner();
    }, 0);
  }

  private updateUnsavedIndicator() {
    const hasChanges = this.hasUnsavedChanges();
    this.unsavedIndicatorTarget.classList.toggle("hidden", !hasChanges);
  }

  private hasUnsavedChanges(): boolean {
    const checkboxes = this.formTarget.querySelectorAll<HTMLInputElement>(".permission-matrix-checkbox");
    for (const cb of checkboxes) {
      const initial = this.initialState.get(cb.name);
      if (initial !== cb.checked) {
        return true;
      }
    }
    return false;
  }

  private parseSubscriptions(): Subscription[] {
    try {
      return JSON.parse(this.subscriptionsValue || "[]");
    } catch {
      return [];
    }
  }

  private countChecked(subscriptionId: number): number {
    const checkboxes = this.formTarget.querySelectorAll<HTMLInputElement>(
      `.permission-matrix-checkbox[data-permission-matrix-subscription-id-param="${subscriptionId}"]`
    );
    let count = 0;
    checkboxes.forEach(cb => { if (cb.checked) count += 1; });
    return count;
  }

  private updateBanner(): boolean {
    if (!this.hasBannerTarget || !this.hasBannerListTarget) return false;

    let anyOverLimit = false;

    this.parseSubscriptions().forEach((sub) => {
      const li = this.bannerListTarget.querySelector<HTMLElement>(
        `li[data-subscription-id="${sub.id}"]`
      );
      if (!li) return;

      if (sub.limit === null || sub.limit === undefined) {
        li.classList.add("hidden");
        return;
      }

      const checkedCount = this.countChecked(sub.id);
      if (checkedCount > sub.limit) {
        const usedEl = li.querySelector<HTMLElement>("[data-role='used']");
        if (usedEl) usedEl.textContent = String(checkedCount);
        li.classList.remove("hidden");
        anyOverLimit = true;
      } else {
        li.classList.add("hidden");
      }
    });

    this.bannerTarget.classList.toggle("hidden", !anyOverLimit);
    return anyOverLimit;
  }

  private validateForm(event: Event) {
    const hasOverLimit = this.updateBanner();
    if (hasOverLimit) {
      event.preventDefault();
      if (this.hasBannerTarget) {
        this.bannerTarget.scrollIntoView({behavior: "smooth", block: "center"});
      }
      return false;
    }
    return true;
  }
}
